import { useQueryClient } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'

import { useGetPlatforms } from '../../../../api/getPlatforms'
import { CampaignSettings } from './CampaignSettings/CampaignSettings'

import { ConfirmNoAdSets } from './ConfirmNoAdSets/ConfirmNoAdSets'
import { ConnectMetaAccount } from './ConnectAccount/ConnectAccount'
import { DomainToPixel } from './DomainToPixel/DomainToPixel'
import { PaginatedSelectAdSets } from './PaginatedSelectAdSets/PaginatedSelectAdSets'
import { SelectAccountIdPaginated } from './SelectAccountIdPaginated/SelectAccountIdPaginated'
// DISABLED UNTIL FB APPROVES OUR APP
// import { SelectBusinessManager } from './SelectBusinessManager/SelectBusinessManager'
import { SelectPixelId } from './SelectPixelID/SelectPixelID'

interface ConnectModalAccountContentProps {
  currentStep: number
  setStep: (s: number) => void
  closeModal: () => void
  onBack: () => void
  isBackDisabled?: boolean
}

export const MetaConnectModalContent = ({
  currentStep,
  setStep,
  closeModal,
  onBack,
  isBackDisabled,
}: ConnectModalAccountContentProps) => {
  const { data } = useGetPlatforms()
  const queryClient = useQueryClient()

  const connectionId = useMemo(() => {
    // get's connectionId from getPlatforms query if user already connected or from connectionForm
    // insures that even if user refreshes page, user can finish wizard or edit connection
    if (!data) {
      return undefined
    } else {
      const account = data.find((platform) => platform.platformType === 'Meta')

      return account?.connectionId ? account.connectionId : undefined
    }
  }, [data])

  const [isAutoProtected, setIsAutoProtected] = useState<boolean>(false)

  const setAccountId = () => {
    setStep(2)
  }

  // DISABLED UNTIL FB APPROVES OUR APP, ON APPROVE - ALL STEPS and METHODS +1
  // const setSelectedManagers = () => {
  //   setStep(3)
  // }

  const setSelectedAccounts = () => {
    setStep(3)
  }

  const setPixelsId = () => {
    setStep(4)
  }

  const setDomains = () => {
    setStep(5)
  }

  const setCampaignSettings = () => {
    setStep(6)
  }

  const setAdSets = (adSets: number[]) => {
    if (adSets.length === 0 && !isAutoProtected) {
      setStep(7)
    } else {
      void queryClient.invalidateQueries({ queryKey: ['getPlatformsConnections'] })
      void queryClient.invalidateQueries({ queryKey: ['platforms'] })
      closeModal()
    }
  }

  const finishWizard = () => {
    setStep(2)
    closeModal()
    void queryClient.invalidateQueries({ queryKey: ['platforms'] })
  }

  if (connectionId) {
    switch (currentStep) {
      // DISABLED UNTIL FB APPROVES OUR APP
      // case 2:
      //   return (
      //     <SelectBusinessManager
      //       onSubmit={setSelectedManagers}
      //       accountId={connectionId}
      //       onBack={onBack}
      //       isBackDisabled={isBackDisabled}
      //     />
      //   )
      case 2: {
        const props = {
          onBack,
          onSubmit: setSelectedAccounts,
          accountId: connectionId,
          isBackDisabled: isBackDisabled,
        }

        return <SelectAccountIdPaginated {...props} />
      }
      case 4:
        return (
          <DomainToPixel
            onSubmit={setDomains}
            accountId={connectionId}
            onBack={onBack}
            isBackDisabled={isBackDisabled}
          />
        )
      case 5:
        return (
          <CampaignSettings
            onBack={onBack}
            accountId={connectionId}
            isBackDisabled={isBackDisabled}
            onSubmit={setCampaignSettings}
          />
        )
      case 6: {
        const props = {
          accountId: connectionId,
          isBackDisabled,
          onBack,
          onSubmit: setAdSets,
          setIsAutoProtected,
        }

        return <PaginatedSelectAdSets {...props} />
      }
      case 7:
        return (
          <ConfirmNoAdSets
            onBack={() => setStep(4)}
            onConfirm={finishWizard}
            isBackDisabled={isBackDisabled}
          />
        )
      default:
        return (
          <SelectPixelId
            onSubmit={setPixelsId}
            onSkipNextStep={setDomains}
            accountId={connectionId}
            onBack={onBack}
            isBackDisabled={isBackDisabled}
          />
        )
    }
  } else {
    return <ConnectMetaAccount submitPlatformId={setAccountId} onClose={closeModal} />
  }
}
